export type BoundingBox = {
  x: number;
  y: number;
  width: number;
  height: number;
  pageIndex?: number;
};

export type RegionType =
  | 'section-instruction'
  | 'section-content'
  | 'question'
  | 'mc-question'
  | 'answer-text'
  | 'answer-mc'
  | 'answer-symbol'
  | 'answer-drawing';

export type QuestionRegion = {
  questionId: string;
  questionLabel?: string;
  type?: RegionType;
  parentSection?: string;
  parentQuestion?: string;
  pageIndex: number;
  bbox: BoundingBox;
  confidence: number;
  regionIndex?: number;
  questionContent?: string;
  questionType?: string;
  transcription?: string;
  selectedOption?: string;
  markType?: string;
  symbolType?: string;
  description?: string;
};

export type QuestionIdentifierResult = {
  regions: QuestionRegion[];
  pageCount: number;
  confidence: number;
  raw?: unknown;
};

export type ExtractedQuestion = {
  questionId: string;
  sectionInstruction: string;
  questionText: string;
  questionType:
    | 'multiple_choice'
    | 'true_false'
    | 'fill_in_blank'
    | 'fill_in_blank_without_options'
    | 'short_answer'
    | 'reading_comprehension'
    | 'essay'
    | 'ordering'
    | 'matching'
    | 'punctuation';
  studentAnswer: string;
  confidence: number;
  // Essay-specific settings (only present when questionType === 'essay')
  essay_custom_criteria?: string;
  essay_instructions?: string;
  essay_max_marks?: number;
};

export type RubricCriterion = {
  id: string;
  description: string;
  points: number;
};

export type QuestionRubric = {
  questionId: string;
  gradingMode: 'auto' | 'rubric';
  maxMarks: number;
  modelAnswer?: string;
  criteria?: RubricCriterion[];
  notes?: string;
};

export type GraderQuestionResult = {
  questionId: string;
  correct: boolean | null;
  marksAwarded: number;
  maxMarks: number;
  modelAnswer?: string;
  feedback: string;
  criteriaResults?: Array<{
    criterionId: string;
    awarded: number;
    maxPoints: number;
    comment: string;
  }>;
  confidence: number;
};

export type GraderResult = {
  questions: GraderQuestionResult[];
  totalMarks: number;
  maxTotalMarks: number;
  overallFeedback: string;
  raw?: unknown;
};

export function extractQuestionsFromRegions(regions: QuestionRegion[]): ExtractedQuestion[] {
  const sectionInstructions = new Map<string, string>();
  // Maps parentSection ID → OCR'd passage/content text for that section
  const sectionContentBySection = new Map<string, string>();

  for (const r of regions) {
    if (r.type === 'section-instruction') {
      sectionInstructions.set(r.questionId, r.questionLabel || '');
    }
    if (r.type === 'section-content' && r.questionContent) {
      // Index by parentSection if present, otherwise derive from questionId (e.g. "S3-content" → "S3-ins")
      const sectionKey = r.parentSection || r.questionId.replace(/-content$/, '-ins');
      const existing = sectionContentBySection.get(sectionKey) || '';
      sectionContentBySection.set(sectionKey, existing ? existing + '\n\n' + r.questionContent : r.questionContent);
    }
  }

  const questions: ExtractedQuestion[] = [];

  for (const r of regions) {
    if (r.type !== 'question' && r.type !== 'mc-question') continue;

    const answerRegion = regions.find(
      (a) =>
        (a.type === 'answer-text' || a.type === 'answer-mc') &&
        a.parentQuestion === r.questionId
    );

    const sectionIns = r.parentSection
      ? sectionInstructions.get(r.parentSection) || ''
      : '';


    let questionType: ExtractedQuestion['questionType'] = 'short_answer';
    if (r.questionType === 'essay') {
      // AI explicitly flagged this region as an essay question (e.g. 選作作文卷)
      questionType = 'essay';
    } else if (r.type === 'mc-question' || answerRegion?.type === 'answer-mc') {
      questionType = 'multiple_choice';
    } else if (
      sectionIns.includes('是非') ||
      sectionIns.includes('對錯') ||
      sectionIns.includes('判斷')
    ) {
      questionType = 'true_false';
    } else if (
      sectionIns.includes('標點符號') ||
      sectionIns.includes('標點')
    ) {
      questionType = 'punctuation';
    } else if (
      sectionIns.includes('排序') ||
      sectionIns.includes('重組') ||
      sectionIns.includes('排列')
    ) {
      questionType = 'ordering';
    } else if (
      sectionIns.includes('配對') ||
      sectionIns.includes('配合') ||
      sectionIns.includes('連線')
    ) {
      questionType = 'matching';
    } else if (
      sectionIns.includes('不供詞') ||
      sectionIns.includes('不提供詞語')
    ) {
      questionType = 'fill_in_blank_without_options';
    } else if (
      (r.questionLabel || '').includes('____') ||
      sectionIns.includes('填充') ||
      sectionIns.includes('填上') ||
      sectionIns.includes('量詞') ||
      (sectionIns.includes('填') && !sectionIns.includes('填寫'))
    ) {
      questionType = 'fill_in_blank';
    } else if (
      sectionIns.includes('閱讀理解') ||
      sectionIns.includes('閱義理解') ||
      sectionIns.includes('閱讀') ||
      sectionIns.includes('理解')
    ) {
      questionType = 'reading_comprehension';
    } else if (
      sectionIns.includes('作文') ||
      sectionIns.includes('寫作') ||
      sectionIns.includes('短文')
    ) {
      questionType = 'essay';
    } else {
      questionType = 'short_answer';
    }

    const studentAnswer = answerRegion
      ? answerRegion.type === 'answer-mc'
        ? answerRegion.selectedOption || ''
        : answerRegion.transcription || ''
      : '';

    // Build questionText: prefer questionContent (full OCR'd text) over label
    let questionText = r.questionContent || r.questionLabel || '';
    // Append section passage content if available (e.g. punctuation exercise passage,
    // reading comprehension passage) so the rubric suggester has full context
    const passageContent = r.parentSection
      ? sectionContentBySection.get(r.parentSection)?.trim()
      : undefined;
    if (passageContent) {
      questionText = questionText
        ? `${questionText}\n\n[原文]\n${passageContent}`
        : passageContent;
    }

    questions.push({
      questionId: r.questionId,
      sectionInstruction: sectionIns,
      questionText,
      questionType,
      studentAnswer,
      confidence: answerRegion?.confidence ?? r.confidence,
    });
  }

  return questions;
}
